<template lang="pug">
  v-container(data-cy="short-code-editor")
    v-form(ref="form" v-model="valid")
      template(v-for="field in fields")
        v-select(
          v-if="field.isDropdown"
          v-model="shortCode[field.value]"
          :label="field.label"
          :hint="field.hint"
          :key="field.value"
          :items="field.items"
          :data-cy="`select-${field.value}`"
          @input="onInput"
        )
        v-text-field(
          v-else
          v-model="shortCode[field.value]"
          :label="field.label"
          :hint="field.hint"
          :key="field.value"
          :data-cy="`input-${field.value}`"
          :rules="[field.validations || true]"
          @input="onInput"
          validate-on-blur
        )

</template>

<script>
import TitleBar from '@/Core/components/TitleBar';
import { restrictedShortCodes } from '@/Communications/config/short-codes';

export default {
  components: {
    TitleBar,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    action: {
      type: String,
      default: '',
    },
    campaigns: {
      type: Array,
      default: () => [],
    },
    contactSources: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      valid: false,
    };
  },
  watch: {
    value(newValue) {
      this.$refs.form.resetValidation();
      if (!this.isEditing) this.$refs.form.reset();
    },
  },
  methods: {
    /**
     * Should return true if:
     *   - userInput = restricted short code name AND one of:
     *     - creating a new short code OR
     *     - is editing a short code's name (oldShortCode !== userInput)
     *
     * @param {String} userInput
     * @return {Boolean}
     */
    isInvalidShortCodeName(userInput) {
      const oldShortCode = this.value.short_code;
      const isEditing = !!this.shortCode.id;

      const isRestrictedShortCode = restrictedShortCodes.includes(userInput);
      const isEditingShortCode = (isEditing && oldShortCode !== userInput);

      return (!isEditing || isEditingShortCode) && isRestrictedShortCode;
    },
    isInvalidUrl(userInput) {
      return userInput && !userInput.match(/^(http[s]?:\/\/).+/i);
    },
    save() {
      if (this.$refs.form.validate()) {
        // only send the following fields to the backend
        const { shortCode } = this;
        const params = {
          callback_url: shortCode.callback_url || null,
          automated_response: shortCode.automated_response,
          contact_source_id: shortCode.contact_source_id || 0,
          campaign_id: shortCode.campaign_id || 0,
        };

        if (params.callback_url) params.callback_url = params.callback_url.trim();

        /**
         * for edit only - only add short_code to params if it's different from the
         * original to avoid duplicate name errors
         */
        if (this.isNotDuplicatedShortCode) params.short_code = shortCode.short_code.trim();
        if (this.isEditing) params.id = shortCode.id;

        this.$emit('saved', params);
      }
    },
    onInput() {
      this.$emit('content-valid', this.valid);
    },
  },
  computed: {
    shortCode() {
      return Object.assign({}, this.value);
    },
    isEditing() {
      return this.action === 'Edit';
    },
    isNotDuplicatedShortCode() {
      if (this.isEditing) {
        return this.shortCode.short_code.trim() !== this.value.short_code;
      }
      return true;
    },
    fields() {
      return [
        {
          label: 'Short Code',
          hint: 'No spaces. Code must be unique - shorter is better!',
          value: 'short_code',
          validations: value => {
            if (this.isInvalidShortCodeName(value)) {
              return 'You cannot use info, stop, stopall, unsubscribe, cancel, end, quit, or start as your short codes.';
            }
            return true;
          },
        },
        {
          label: 'Long URL',
          hint: 'ex. http://www.yourwebsite.com/property.html',
          value: 'callback_url',
          validations: value => {
            if (this.isInvalidUrl(value)) {
              return 'Please enter a valid URL start with http:// or https://';
            }
            return true;
          },
        },
        {
          label: 'Text Auto Reply',
          hint: 'Max 1000 characters. This is the automated response texted back to your client.',
          value: 'automated_response',
        },
        {
          label: 'Contact Source (optional)',
          hint: 'Select Contact Source',
          value: 'contact_source_id',
          isDropdown: true,
          items: this.contactSources,
        },
        {
          label: 'Follow Campaign (optional)',
          hint: 'Select Campaign',
          value: 'campaign_id',
          isDropdown: true,
          items: this.campaigns,
        },
      ];
    },
  },
};
</script>
